import { ChangeEvent, useEffect, useState } from 'react'
import { NavLink, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Breadcrumbs as MuiBreadcrumbs,
  Divider as MuiDivider,
  Grid,
  Link,
  Typography,
  Skeleton,
  Box,
  Tooltip,
  Button
} from '@mui/material'
import { spacing } from '@mui/system'
import { styled } from '@mui/material/styles'
import useAuth from '../../hooks/useAuth'
import {
  getRuuviTag,
  getRuuviTagAccelerationAggregates,
  getRuuviTagAccelerationNow,
  getRuuviTagAccelerations,
  getRuuviTagBatteryNow,
  getRuuviTagConditionAggregates,
  getRuuviTagConditionNow,
  getRuuviTagConditions,
  getRuuviTagMovementNow
} from '../../services/ruuviTagService'
import {
  RuuviTag,
  RuuviTagAcceleration,
  RuuviTagAccelerationQuery,
  RuuviTagAccelerationAggregate,
  RuuviTagAccelerationAggregateQuery,
  RuuviTagBattery,
  RuuviTagCondition,
  RuuviTagConditionQuery,
  RuuviTagConditionAggregate,
  RuuviTagConditionAggregateQuery,
  RuuviTagMovement,
  DateRange
} from '../../types'
import {
  Hours,
  RuuviTagAccelerationAggregateInterval,
  RuuviTagConditionAggregateInterval
} from '../../enums'
import RuuviTagAccelerationAxisChart from '../../components/RuuviTagAccelerationAxisChart'
import RuuviTagBatteryVoltageStats from '../../components/RuuviTagBatteryVoltageStats'
import RuuviTagHumidityChart from '../../components/RuuviTagHumidityChart'
import RuuviTagHumidityStats from '../../components/RuuviTagHumidityStats'
import RuuviTagPressureChart from '../../components/RuuviTagPressureChart'
import RuuviTagPressureStats from '../../components/RuuviTagPressureStats'
import RuuviTagSignalChart from '../../components/RuuviTagSignalChart'
import RuuviTagSignalStats from '../../components/RuuviTagSignalStats'
import RuuviTagTemperatureChart from '../../components/RuuviTagTemperatureChart'
import RuuviTagTemperatureStats from '../../components/RuuviTagTemperatureStats'
import { differenceInHours, isBefore } from 'date-fns'
import { useSelector } from 'react-redux'
import { RootState } from '../../redux'
import RuuviTagDetails from '../../components/RuuviTagDetails'
// import RuuviTagMovementCountStats from '../../components/RuuviTagMovementCountStats'
import DateRangeBar from '../../components/DateRangeBar'
import { formatMacAddress } from '../../utils/string'
import PageHeader from '../../components/PageHeader'
import RuuviTagMenu from '../../components/RuuviTagMenu'
import useLoadingAlerts from '../../hooks/useLoadingAlerts'
import { usePolicy } from '../../contexts/PolicyContext'
import { useAssetDateRange } from '../../hooks/asset'
import { humanizeDateRange } from '../../utils/date'
import DateRangeLimitSwitch from '../../components/DateRangeLimitSwitch'
import RuuviTagPageTitle from '../../components/RuuviTagPageTitle'
import RuuviTagActiveAlertsWarning from '../../components/RuuviTagActiveAlertsWarning'
import RuuviTagDewPointStats from '../../components/RuuviTagDewPointStats'
import RuuviTagDewPointChart from '../../components/RuuviTagDewPointChart'
import { useDateRangeQuery } from '../../hooks/useQuery'

const Divider = styled(MuiDivider)(spacing)

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing)

export default function RuuviTagPage() {
  /**
   * The route params.
   */
  const { id } = useParams()

  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The current user.
   */
  const { currentUser } = useAuth()

  /**
   * The policy state.
   */
  const { isSuperadmin, isAdminOfCompany } = usePolicy()

  /**
   * The current date range.
   */
  const { autoRefresh } = useSelector((state: RootState) => state.query)

  /**
   * The RuuviTag.
   */
  const [ruuviTag, setRuuviTag] = useState<RuuviTag | null>(null)

  const dateRange = useDateRangeQuery()

  const assetDateRange = useAssetDateRange(ruuviTag?.asset)

  /**
   * Indicates if the current user can control limited date range.
   */
  const canControlLimitedDateRange =
    isSuperadmin || (ruuviTag?.asset?.companyId && isAdminOfCompany(ruuviTag.asset.companyId))

  /**
   * Indicates if the user wants to limit the date range.
   */
  const [limitDateRange, setLimitDateRange] = useState<boolean>(true)

  /**
   * Handle the change event for limiting the date range.
   */
  const handleLimitDateRangeChange = (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setLimitDateRange(checked)
  }

  /**
   * The limited date range based on asset.
   */
  const limitedDateRange = limitDateRange ? assetDateRange : dateRange

  /**
   * The latest acceleration of the RuuviTag.
   */
  const [_accelerationNow, setAccelerationNow] = useState<RuuviTagAcceleration | null>(null)

  /**
   * The latest battery of the RuuviTag.
   */
  const [batteryNow, setBatteryNow] = useState<RuuviTagBattery | null>(null)

  /**
   * The latest condition of the RuuviTag.
   */
  const [conditionNow, setConditionNow] = useState<RuuviTagCondition | null>(null)

  /**
   * The latest movement of the RuuviTag.
   */
  const [_movementNow, setMovementNow] = useState<RuuviTagMovement | null>(null)

  /**
   * The accelerations of the RuuviTag.
   */
  const [accelerations, setAccelerations] = useState<RuuviTagAcceleration[]>([])

  /**
   * The conditions of the RuuviTag.
   */
  const [conditions, setConditions] = useState<RuuviTagCondition[]>([])

  /**
   * The accceleration aggregates of the RuuviTag.
   */
  const [accelerationAggregates, setAccelerationAggregates] = useState<
    RuuviTagAccelerationAggregate[]
  >([])

  /**
   * The condition aggregates of the RuuviTag sensor.
   */
  const [conditionAggregates, setConditionAggregates] = useState<RuuviTagConditionAggregate[]>([])

  /**
   * The alerts state.
   */
  const { alerts } = useLoadingAlerts({
    ruuvitag: id,
    source: 'prometheus'
  })

  /**
   * Indicates if the component is loading.
   */
  const [isLoading, setIsLoading] = useState<boolean>(true)

  /**
   * The differencec of date range in hours.
   */
  const differenceOfDateRangeInHours = Math.abs(differenceInHours(dateRange.from, dateRange.to))

  /**
   * Indicates if aggregates should be used.
   */
  const isUsingAggregates = differenceOfDateRangeInHours >= 24

  /**
   * The query for the accelerations of the RuuviTag.
   */
  const accelerationQuery: RuuviTagAccelerationQuery = {
    from: limitedDateRange.from,
    to: limitedDateRange.to
  }

  /**
   * The query for the accelerations of the RuuviTag.
   */
  const accelerationAggregateQuery: RuuviTagAccelerationAggregateQuery = {
    from: limitedDateRange.from,
    to: limitedDateRange.to,
    interval: getRuuviTagAccelerationAggregateInterval()
  }

  /**
   * The query for the conditions of the RuuviTag.
   */
  const conditionQuery: RuuviTagConditionQuery = {
    from: limitedDateRange.from,
    to: limitedDateRange.to
  }

  /**
   * The query for the conditions of the RuuviTag.
   */
  const conditionAggregateQuery: RuuviTagConditionAggregateQuery = {
    from: limitedDateRange.from,
    to: limitedDateRange.to,
    interval: getRuuviTagConditionAggregateInterval()
  }

  /**
   * Boots the component.
   */
  async function boot(signal: AbortSignal): Promise<void> {
    try {
      if (!autoRefresh) {
        setIsLoading(true)
      }

      if (ruuviTag === null) {
        setIsLoading(true)
        await loadRuuviTag(signal)
      }

      await Promise.all([
        loadAccelerationNow(signal),
        loadConditionNow(signal),
        loadBatteryNow(signal),
        loadMovementNow(signal)
      ])

      if (isUsingAggregates) {
        await Promise.all([loadAccelerationAggregates(signal), loadConditionAggregates(signal)])
      } else {
        await Promise.all([loadAccelerations(signal), loadConditions(signal)])
      }
    } catch (_error) {
      // TODO: Handle errors.
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Loads the RuuviTag.
   */
  async function loadRuuviTag(signal: AbortSignal): Promise<void> {
    if (id) {
      setRuuviTag(await getRuuviTag(id, { signal }))
    }
  }

  /**
   * Loads the accelerations of the RuuviTag.
   */
  async function loadAccelerations(signal: AbortSignal): Promise<void> {
    if (id) {
      setAccelerations(
        await getRuuviTagAccelerations(id, accelerationQuery, {
          signal
        })
      )
    }
  }

  /**
   * Loads the conditions of the RuuviTag.
   */
  async function loadConditions(signal: AbortSignal): Promise<void> {
    if (id) {
      setConditions(await getRuuviTagConditions(id, conditionQuery, { signal }))
    }
  }

  /**
   * Loads the acceleration agggregates of the RuuviTag.
   */
  async function loadAccelerationAggregates(signal: AbortSignal): Promise<void> {
    if (id) {
      setAccelerationAggregates(
        await getRuuviTagAccelerationAggregates(id, accelerationAggregateQuery, { signal })
      )
    }
  }

  /**
   * Loads the condition aggregates of the RuuviTag.
   */
  async function loadConditionAggregates(signal: AbortSignal): Promise<void> {
    if (id) {
      setConditionAggregates(
        await getRuuviTagConditionAggregates(id, conditionAggregateQuery, {
          signal
        })
      )
    }
  }

  /**
   * Loads the current acceleration of the RuuviTag.
   */
  async function loadAccelerationNow(signal: AbortSignal): Promise<void> {
    if (id) {
      setAccelerationNow(
        await getRuuviTagAccelerationNow(id, {
          signal
        })
      )
    }
  }

  /**
   * Loads the current battery of the RuuviTag.
   */
  async function loadBatteryNow(signal: AbortSignal): Promise<void> {
    if (id) {
      setBatteryNow(
        await getRuuviTagBatteryNow(id, {
          signal
        })
      )
    }
  }

  /**
   * Loads the current condition of the RuuviTag.
   */
  async function loadConditionNow(signal: AbortSignal): Promise<void> {
    if (id) {
      setConditionNow(
        await getRuuviTagConditionNow(id, {
          signal
        })
      )
    }
  }

  /**
   * Loads the current movement of the RuuviTag.
   */
  async function loadMovementNow(signal: AbortSignal): Promise<void> {
    if (id) {
      setMovementNow(
        await getRuuviTagMovementNow(id, {
          signal
        })
      )
    }
  }

  /**
   * Get the aggregate interval for the accelerations of the RuuviTag.
   */
  function getRuuviTagAccelerationAggregateInterval(): RuuviTagAccelerationAggregateInterval {
    if (differenceOfDateRangeInHours < Hours.THREE_DAYS) {
      return RuuviTagAccelerationAggregateInterval.FIVE_MINUTES
    } else if (differenceOfDateRangeInHours < Hours.SEVEN_DAYS) {
      return RuuviTagAccelerationAggregateInterval.FIFTEEN_MINUTES
    } else if (differenceOfDateRangeInHours <= Hours.THIRTY_ONE_DAYS) {
      return RuuviTagAccelerationAggregateInterval.ONE_HOUR
    } else {
      return RuuviTagAccelerationAggregateInterval.ONE_DAY
    }
  }

  /**
   * Get the aggregate interval for the conditions of the RuuviTag.
   */
  function getRuuviTagConditionAggregateInterval(): RuuviTagConditionAggregateInterval {
    if (differenceOfDateRangeInHours < Hours.THREE_DAYS) {
      return RuuviTagConditionAggregateInterval.FIVE_MINUTES
    } else if (differenceOfDateRangeInHours < Hours.SEVEN_DAYS) {
      return RuuviTagConditionAggregateInterval.FIFTEEN_MINUTES
    } else if (differenceOfDateRangeInHours <= Hours.THIRTY_ONE_DAYS) {
      return RuuviTagConditionAggregateInterval.ONE_HOUR
    } else {
      return RuuviTagConditionAggregateInterval.ONE_DAY
    }
  }

  /**
   * Boot the component when id or date range changes.
   */
  useEffect(() => {
    const controller = new AbortController()
    if (currentUser && id) {
      boot(controller.signal)
    }
    return () => controller.abort()
  }, [currentUser, id, dateRange])

  return (
    <>
      <PageHeader
        title={t('conditionMonitoring')}
        heading={
          ruuviTag === null ? (
            <Skeleton animation="wave" variant="text" width="120px" sx={{ fontSize: '1.5rem' }} />
          ) : (
            <RuuviTagPageTitle ruuviTag={ruuviTag} />
          )
        }
        breadcrumbs={
          <Breadcrumbs>
            {ruuviTag && ruuviTag?.asset?.project?.name && (
              <Link component={NavLink} to={`/projects/${ruuviTag.asset.projectId}`}>
                {ruuviTag.asset?.project?.name}
              </Link>
            )}

            {ruuviTag && ruuviTag?.asset?.project?.name ? (
              <Link component={NavLink} to={`/projects/${ruuviTag.asset.projectId}#monitoring `}>
                {t('deviceMonitoring')}
              </Link>
            ) : ruuviTag ? (
              <Link component={NavLink} to="/ruuvi-tags">
                {t('conditionMonitoring')}
              </Link>
            ) : (
              <Skeleton animation="wave" variant="text" width="40px" height="1em" />
            )}

            {ruuviTag ? (
              <Typography>{formatMacAddress(ruuviTag.id)}</Typography>
            ) : (
              <Skeleton animation="wave" variant="text" width="40px" height="1em" />
            )}
          </Breadcrumbs>
        }
        action={<>{ruuviTag && <RuuviTagMenu ruuviTag={ruuviTag} onUpdate={setRuuviTag} />}</>}
      />

      <Box my={6}>
        <DateRangeBar
          includeOneHourOption={true}
          showAutoRefreshSwitch={true}
          customControl={
            canControlLimitedDateRange && (
              <Tooltip title={humanizeDateRange(limitedDateRange.from, limitedDateRange.to)}>
                <DateRangeLimitSwitch
                  size="small"
                  value={limitDateRange}
                  checked={limitDateRange}
                  onChange={handleLimitDateRangeChange}
                />
              </Tooltip>
            )
          }
        />
      </Box>

      <Grid container spacing={6}>
        <Grid item xs={12}>
          {ruuviTag && (
            <>
              {alerts.length > 0 && (
                <RuuviTagActiveAlertsWarning
                  ruuviTag={ruuviTag}
                  alerts={alerts}
                  action={
                    <Button
                      color="warning"
                      component={NavLink}
                      to={`/ruuvi-tags/${ruuviTag.id}/alerts`}
                    >
                      {t('showAlerts')}
                    </Button>
                  }
                  sx={{ mb: 6, width: '100%' }}
                />
              )}

              <RuuviTagDetails ruuviTag={ruuviTag} onUpdate={(ruuviTag) => setRuuviTag(ruuviTag)} />
              <Divider sx={{ my: 6 }} />
            </>
          )}

          <Grid container spacing={6} mb={6}>
            {ruuviTag?.has_temperature_sensor && (
              <Grid item xs={12} lg={4}>
                <RuuviTagTemperatureStats
                  ruuviTag={ruuviTag}
                  ruuviTagCondition={conditionNow}
                  alerts={alerts}
                  loading={isLoading}
                />
              </Grid>
            )}

            {ruuviTag?.has_humidity_sensor && (
              <Grid item xs={12} lg={4}>
                <RuuviTagHumidityStats
                  ruuviTag={ruuviTag}
                  ruuviTagCondition={conditionNow}
                  alerts={alerts}
                  loading={isLoading}
                />
              </Grid>
            )}

            {ruuviTag?.has_temperature_sensor && ruuviTag?.has_humidity_sensor && (
              <Grid item xs={12} lg={4}>
                <RuuviTagDewPointStats
                  ruuviTag={ruuviTag}
                  ruuviTagCondition={conditionNow}
                  alerts={alerts}
                  loading={isLoading}
                />
              </Grid>
            )}

            {ruuviTag?.has_pressure_sensor && (
              <Grid item xs={12} lg={4}>
                <RuuviTagPressureStats
                  ruuviTag={ruuviTag}
                  ruuviTagCondition={conditionNow}
                  alerts={alerts}
                  loading={isLoading}
                />
              </Grid>
            )}

            {/*<Grid item xs={12} lg={4}>
              <RuuviTagMovementCountStats
                ruuviTagMovement={movementNow}
                loading={isLoading}
              />
              </Grid>*/}

            <Grid item xs={12} lg={4}>
              <RuuviTagBatteryVoltageStats
                ruuviTagBattery={batteryNow}
                ruuviTagCondition={conditionNow}
                loading={isLoading}
              />
            </Grid>

            <Grid item xs={12} lg={4}>
              <RuuviTagSignalStats ruuviTagCondition={conditionNow} loading={isLoading} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 6 }} />

          <Grid container spacing={6}>
            {ruuviTag?.has_temperature_sensor && (
              <Grid item xs={12}>
                <RuuviTagTemperatureChart
                  conditions={isUsingAggregates ? conditionAggregates : conditions}
                  query={isUsingAggregates ? conditionAggregateQuery : conditionQuery}
                  dateRange={dateRange}
                  loading={isLoading}
                />
              </Grid>
            )}

            {ruuviTag?.has_humidity_sensor && (
              <Grid item xs={12}>
                <RuuviTagHumidityChart
                  conditions={isUsingAggregates ? conditionAggregates : conditions}
                  query={isUsingAggregates ? conditionAggregateQuery : conditionQuery}
                  dateRange={dateRange}
                  loading={isLoading}
                />
              </Grid>
            )}

            {ruuviTag?.has_temperature_sensor && ruuviTag?.has_humidity_sensor && (
              <Grid item xs={12}>
                <RuuviTagDewPointChart
                  conditions={isUsingAggregates ? conditionAggregates : conditions}
                  query={isUsingAggregates ? conditionAggregateQuery : conditionQuery}
                  dateRange={dateRange}
                  loading={isLoading}
                />
              </Grid>
            )}

            {ruuviTag?.has_pressure_sensor && (
              <Grid item xs={12}>
                <RuuviTagPressureChart
                  conditions={isUsingAggregates ? conditionAggregates : conditions}
                  query={isUsingAggregates ? conditionAggregateQuery : conditionQuery}
                  dateRange={dateRange}
                  loading={isLoading}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <RuuviTagAccelerationAxisChart
                accelerations={isUsingAggregates ? accelerationAggregates : accelerations}
                query={isUsingAggregates ? accelerationAggregateQuery : accelerationQuery}
                dateRange={dateRange}
                loading={isLoading}
              />
            </Grid>

            <Grid item xs={12}>
              <RuuviTagSignalChart
                conditions={isUsingAggregates ? conditionAggregates : conditions}
                query={isUsingAggregates ? conditionAggregateQuery : conditionQuery}
                dateRange={dateRange}
                loading={isLoading}
              />
            </Grid>

            {/*<Grid item xs={12}>
              <RuuviTagBatteryVoltageChart
                batteries={
                  isUsingAggregates ? batteryAggregates : batteries
                }
                dateRange={dateRange}
              />
            </Grid>*/}

            {/*<Grid item xs={12}>
              <RuuviTagMovementCountChart
                movements={movements}
                query={movementQuery}
                dateRange={dateRange}
              />
            </Grid>*/}
          </Grid>
        </Grid>
      </Grid>
    </>
  )
}
